import '@mantine/core/styles.css';
import { createTheme, MantineProvider } from '@mantine/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createApiClient, retryQuery } from './api/client.js';
import { createCognitoClient } from './auth/cognito.js';
import { browserStorage, createCognitoAuthStore, createDevAuthStore } from './auth/session.js';
import { readConfig } from './config.js';
import { createAppRouter } from './router.js';
import { ServicesProvider, type Services } from './services.js';

const config = readConfig();
const auth =
  import.meta.env.DEV && config.authMode === 'dev'
    ? createDevAuthStore(browserStorage())
    : createCognitoAuthStore(createCognitoClient({ region: config.cognito.region, clientId: config.cognito.clientId }), browserStorage());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Berechtigungs- und Eingabefehler nicht wiederholen.
      retry: retryQuery,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createAppRouter({ auth, queryClient });

const api = createApiClient({
  baseUrl: config.apiUrl,
  getAccessToken: () => auth.getAccessToken(),
  onUnauthorized: () => {
    queryClient.clear();
    const { pathname, href } = router.state.location;
    if (pathname === '/login' || pathname === '/forgot') return;
    void router.navigate({ to: '/login', search: { redirect: href } });
  },
});

const services: Services = { auth, api, authMode: config.authMode };
// Akzentfarbe aus dem Blau des bisherigen PAT (app3 `#325b85`/`#24415f`), nur angedeutet (Flori 01.11.2026).
const theme = createTheme({
  primaryColor: 'pat',
  primaryShade: 6,
  defaultRadius: 'sm',
  colors: {
    pat: ['#edf2f8', '#dae4f0', '#b3c7de', '#89a8cb', '#6690bc', '#4f7fb2', '#3d6c9e', '#325b85', '#24415f', '#172b3e'],
  },
});

// Sitzungswechsel (Abmelden, anderer Benutzer, anderer Tab): zwischengespeicherte
// Mandantendaten verwerfen und Routen neu prüfen.
let previousStatus = auth.getState().status;
auth.subscribe(() => {
  const { status } = auth.getState();
  const wasSignedIn = previousStatus === 'signed-in';
  previousStatus = status;
  if (status === 'signed-in') return;
  queryClient.clear();
  // Beim Start leitet der Router selbst (mit Rücksprungziel) um; hier nur echte Abmeldungen.
  const path = router.state.location.pathname;
  if (status === 'signed-out' && wasSignedIn && path !== '/login' && path !== '/forgot') void router.navigate({ to: '/login' });
  if (status === 'restoring') void router.invalidate();
});

function App() {
  return (
    <MantineProvider theme={theme}>
      <ServicesProvider services={services}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </ServicesProvider>
    </MantineProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('#root fehlt');
createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
